import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResultsSummaryPanel = ({ 
  summary,
  onShare,
  onNewUpload
}) => {
  const getScoreColor = (score) => {
    if (score >= 8) return '#10B981';
    if (score >= 6) return '#9333EA';
    if (score >= 4) return '#F59E0B';
    return '#EF4444';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-3 sm:p-4 space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-foreground mb-0.5">Analysis Summary</h2>
          <p className="text-[10px] sm:text-xs text-muted-foreground">
            {new Date(summary.analyzedDate)?.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onShare}
          iconName="Share2"
          iconPosition="left"
          className="text-xs h-9"
        >
          Share
        </Button>
      </div>

      {/* Premium AI Insights Promo Banner */}
      <div className="bg-gradient-to-r from-purple-600 to-cyan-500 rounded-lg p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="bg-white/20 rounded-full p-1.5 flex-shrink-0">
            <Icon name="Sparkles" size={16} color="#FFFFFF" />
          </div>
          <div className="min-w-0">
            <h4 className="text-white font-semibold text-xs leading-tight">Get personalized tips</h4>
            <p className="text-white/80 text-[10px] leading-tight">AI insights with Premium</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.location.href = '/purchase-credits'}
          className="bg-white text-purple-600 hover:bg-white/90 border-0 flex-shrink-0 text-xs h-8 px-3"
        >
          Try
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-muted/30 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="Image" size={14} color="#9333EA" />
            <span className="text-[10px] text-muted-foreground">Photos</span>
          </div>
          <div className="text-xl font-bold text-foreground">{summary?.totalPhotos}</div>
        </div>

        <div className="bg-muted/30 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="TrendingUp" size={14} color="#10B981" />
            <span className="text-[10px] text-muted-foreground">Average</span>
          </div>
          <div 
            className="text-xl font-bold"
            style={{ color: getScoreColor(summary?.averageScore) }}
          >
            {summary?.averageScore?.toFixed(1)}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="Award" size={14} color="#FFD700" />
            <span className="text-[10px] text-muted-foreground">Highest</span>
          </div>
          <div 
            className="text-xl font-bold"
            style={{ color: getScoreColor(summary?.highestScore) }}
          >
            {summary?.highestScore?.toFixed(1)}
          </div>
        </div>

        <div className="bg-muted/30 rounded-lg p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon name="BarChart3" size={14} color="#06B6D4" />
            <span className="text-[10px] text-muted-foreground">Range</span>
          </div>
          <div className="text-xl font-bold text-foreground">
            {summary?.scoreRange?.toFixed(1)}
          </div>
        </div>
      </div>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Lightbulb" size={16} color="#9333EA" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1.5">Top Tips</h3>
            <ul className="space-y-1.5">
              {summary?.recommendations?.map((rec, index) => (
                <li key={index} className="flex items-start gap-1.5 text-[11px] text-foreground leading-snug">
                  <Icon name="CheckCircle2" size={12} color="#9333EA" className="mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
            <Icon name="Star" size={16} color="#06B6D4" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1">Best Photo</h3>
            <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
              Rank #{summary?.bestPhotoRank} excels in {summary?.bestPhotoStrength}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {summary?.bestPhotoTags?.map((tag, index) => (
                <span 
                  key={index}
                  className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full text-[10px] font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-2 border-t border-border">
        <Button
          variant="default"
          size="lg"
          onClick={onNewUpload}
          iconName="Upload"
          iconPosition="left"
          fullWidth
          className="text-sm h-11"
        >
          Upload New Photos
        </Button>
      </div>
    </div>
  );
};

export default ResultsSummaryPanel;